import React, { FC, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Row, Col, message } from "antd";
import { Controller } from "react-hook-form";
import { useHandleForm } from "../../../../hooks/useHandleForm";
import {
  ExpandedTable,
  IButton,
  IDatePicker,
  IDateTimePicker,
  StoredPagination,
  ITooltip,
} from "../../../../ui-kit";
import sitPanelReports from "../../../../store/sitPanelReportsStore";
import sitPanelDailyViolationsReports from "../../../../store/sitPanelDailyViolationsReportsStore";
import { IReport } from "./SitPanelReports";
import moment from "moment";

import styles from "./SitPanelReports.module.scss";
import ReportForm from "./ReportForm";
import { TrashIco } from "../../../../assets/customIcons";
import Constants from "../../../../constants/Common";

const DailyViolationsReport: FC<IReport> = observer(({ columns }) => {
  const {
    titleIcons: { trash },
  } = Constants;
  const initialFocus = {};
  const { control, errors, handleSubmit, watchAllField } = useHandleForm(
    null,
    null,
    initialFocus,
  );

  sitPanelReports.setTimeTo(moment("08:00", "HH:mm"));

  useEffect(() => {
    if (sitPanelReports.reportStatus) {
      message.info(sitPanelReports.reportStatus);
    }
    sitPanelReports.setActionResult(null);
  }, [sitPanelReports.reportStatus]);

  useEffect(() => {
    sitPanelDailyViolationsReports.getReports();
  }, [
    sitPanelDailyViolationsReports.sorter.changingKey,
    sitPanelDailyViolationsReports.paginator.page,
    sitPanelDailyViolationsReports.paginator.perPage,
  ]);

  const onSubmit = async (values) => {
    await sitPanelReports.downloadDailyViolationsReport({
      dateFrom: values.dateFrom,
      dateTo: values.dateTo,
    });
    await sitPanelDailyViolationsReports.getReports();
  };

  const cols = [...columns];

  const onDeleteReport = async (id) => {
    await sitPanelReports.deleteReport(id);
    await sitPanelDailyViolationsReports.getReports();
  };

  cols.push({
    title: "",
    key: "action",
    render: (text, record, index) => (
      <ITooltip title={trash} placement="right">
        <TrashIco onClick={() => onDeleteReport(record.id)} />
      </ITooltip>
    ),
    className: "delete-cell",
  });

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.SitPanelReportsDateRange}
      >
        <Row gutter={[15, 20]}>
          <Col>
            <Controller
              control={control}
              defaultValue={moment()
                .subtract(1, "days")
                .format("YYYY-MM-DD[T]HH:mm")}
              name="dateFrom"
              rules={{
                validate: (value) => {
                  const diff = moment(value, "YYYY-MM-DD[T]HH:mm").diff(
                    moment(watchAllField.dateTo, "YYYY-MM-DD[T]HH:mm"),
                    "minute",
                  );

                  if (diff > 0 || diff < -1440) {
                    return "Дата должна быть меньше второй на 1 день";
                  }
                },
              }}
              render={({ field }) => (
                <IDateTimePicker
                  field={field}
                  placeholder="От"
                  disableData={(current) =>
                    current > moment(watchAllField.dateTo)
                  }
                  error={errors.dateFrom?.message}
                  dateInputReadOnly={false}
                />
              )}
            />
          </Col>
          <Col>
            <Controller
              control={control}
              defaultValue={moment().format("YYYY-MM-DDTHH:mm")}
              name="dateTo"
              render={({ field }) => (
                <IDateTimePicker
                  field={field}
                  placeholder="До"
                  dateInputReadOnly={false}
                />
              )}
            />
          </Col>
          <Col>
            <IButton>Сформировать</IButton>
          </Col>
        </Row>
      </form>

      <ExpandedTable
        columns={cols}
        sort={sitPanelDailyViolationsReports.sorter}
        data={sitPanelDailyViolationsReports.reports}
        rowKey={(record) => record.id}
        rowsLength={sitPanelDailyViolationsReports.paginator.perPage}
        loading={sitPanelDailyViolationsReports.isLoading.value}
      >
        <ReportForm context={sitPanelDailyViolationsReports} />
      </ExpandedTable>
      <StoredPagination {...sitPanelDailyViolationsReports.paginator} />
    </>
  );
});

export default DailyViolationsReport;
